import { useEffect, useState, useMemo } from "react";
import { X, ArrowUpRight, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import Lightbox from "./Lightbox";
import { resolveImageUrl } from "../lib/api";

export default function ProductDialog({ product, onClose }) {
  const [lightboxAt, setLightboxAt] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!product) return;
    setIdx(0);
    const onKey = (e) => {
      if (e.key === "Escape" && lightboxAt === null) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [product, onClose, lightboxAt]);

  const images = useMemo(() => {
    if (!product) return [];
    const list = (product.images && product.images.length
      ? product.images
      : product.image
      ? [product.image]
      : []
    ).filter(Boolean);
    return list;
  }, [product]);

  // Touch swipe on carousel
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 50) {
      if (dx > 0) setIdx((i) => (i - 1 + images.length) % images.length);
      else setIdx((i) => (i + 1) % images.length);
    }
    setTouchStart(null);
  };

  if (!product) return null;
  const total = images.length;

  return (
    <div
      data-testid="product-dialog"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label={product.name}
    >
      <button
        data-testid="product-dialog-overlay"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute inset-0 bg-[color:var(--tr-ink)]/40 backdrop-blur-sm"
      />

      <div className="relative ml-auto w-full md:max-w-[1100px] h-full bg-[color:var(--tr-bg)] overflow-y-auto tr-rise" style={{ animationDuration: "700ms" }}>
        {/* Close button (always visible) */}
        <button
          data-testid="product-dialog-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-30 flex items-center gap-2 px-3 py-2 bg-[color:var(--tr-bg)]/95 backdrop-blur-sm border border-[color:var(--tr-border)] hover:bg-[color:var(--tr-ink)] hover:text-[color:var(--tr-bg)] transition-colors font-sub text-[0.7rem] uppercase tracking-[0.24em]"
        >
          Chiudi <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-0 items-start">
          {/* Carousel (sticky on desktop, scrolls inside dialog on mobile) */}
          <div className="md:col-span-7 bg-[color:var(--tr-bg-2)] md:sticky md:top-0 md:self-start md:h-[100dvh]">
            {total === 0 ? (
              <div className="aspect-[4/5] md:aspect-auto md:h-full w-full" />
            ) : (
              <div
                className="relative w-full h-full overflow-hidden"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                data-testid="product-carousel"
              >
                {/* Slider track */}
                <div
                  className="flex w-full h-full transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${idx * 100}%)` }}
                >
                  {images.map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      onClick={() => setLightboxAt(i)}
                      aria-label="Apri immagine a tutto schermo"
                      data-testid={`product-image-${i}`}
                      className="group relative shrink-0 w-full h-full bg-[color:var(--tr-bg-2)] cursor-zoom-in"
                    >
                      <div className="tr-img-wrap aspect-[4/5] md:aspect-auto md:h-full w-full">
                        <img src={resolveImageUrl(url)} alt={product.name} />
                      </div>
                      <span
                        className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-[color:var(--tr-bg)]/85 backdrop-blur-sm font-sub text-[0.62rem] uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ color: "#1A1918" }}
                      >
                        <ZoomIn className="w-3 h-3" strokeWidth={1.5} /> Zoom
                      </span>
                    </button>
                  ))}
                </div>

                {/* Arrows */}
                {total > 1 && (
                  <>
                    <button
                      data-testid="carousel-prev"
                      onClick={() => setIdx((i) => (i - 1 + total) % total)}
                      aria-label="Immagine precedente"
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[color:var(--tr-bg)]/90 backdrop-blur-sm border border-[color:var(--tr-border)] hover:bg-[color:var(--tr-ink)] hover:text-[color:var(--tr-bg)] transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" strokeWidth={1.25} />
                    </button>
                    <button
                      data-testid="carousel-next"
                      onClick={() => setIdx((i) => (i + 1) % total)}
                      aria-label="Immagine successiva"
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[color:var(--tr-bg)]/90 backdrop-blur-sm border border-[color:var(--tr-border)] hover:bg-[color:var(--tr-ink)] hover:text-[color:var(--tr-bg)] transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" strokeWidth={1.25} />
                    </button>
                    {/* Dots */}
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 pointer-events-none">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setIdx(i)}
                          aria-label={`Vai a immagine ${i + 1}`}
                          className={`pointer-events-auto h-1.5 rounded-full transition-all ${
                            i === idx ? "w-6 bg-[color:var(--tr-ink)]" : "w-1.5 bg-[color:var(--tr-ink)]/30"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="md:col-span-5">
            <div className="px-5 sm:px-8 md:px-10 lg:px-12 pt-16 sm:pt-20 md:pt-20 lg:pt-24 pb-10 md:pb-16 lg:pb-20">
              <p className="label-eyebrow" data-testid="product-eyebrow">
                N° {product.code} · {product.category} · {product.year}
              </p>
              <h2
                data-testid="product-name"
                className="font-display font-light text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]"
              >
                {product.name}
              </h2>
              {product.description && (
                <p
                  data-testid="product-description"
                  className="font-display italic text-base sm:text-lg md:text-xl mt-5 text-[color:var(--tr-ink-soft)] leading-snug"
                >
                  {product.description}
                </p>
              )}

              <div className="mt-8 sm:mt-10 space-y-6 sm:space-y-8">
                {product.story && (
                  <div>
                    <p className="label-eyebrow mb-3">Storia</p>
                    <p
                      data-testid="product-story"
                      className="font-body text-sm sm:text-base text-[color:var(--tr-ink)] leading-relaxed whitespace-pre-wrap"
                    >
                      {product.story}
                    </p>
                  </div>
                )}

                {product.materials?.length > 0 && (
                  <div>
                    <p className="label-eyebrow mb-3">Materiali</p>
                    <ul className="space-y-2">
                      {product.materials.map((m, i) => (
                        <li
                          key={i}
                          data-testid={`product-material-${i}`}
                          className="font-body text-sm sm:text-base flex items-center justify-between border-b border-[color:var(--tr-border)] pb-2"
                        >
                          <span>{m}</span>
                          <span className="text-[color:var(--tr-accent)] font-display italic">·</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.dimensions && (
                  <div>
                    <p className="label-eyebrow mb-3">Dimensioni</p>
                    <p
                      data-testid="product-dimensions"
                      className="font-body text-sm sm:text-base text-[color:var(--tr-ink)]"
                    >
                      {product.dimensions}
                    </p>
                  </div>
                )}
              </div>

              <button
                data-testid="product-cta-request"
                onClick={() => {
                  onClose();
                  setTimeout(() => {
                    document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" });
                  }, 220);
                }}
                className="tr-btn mt-10"
              >
                Richiedi questo pezzo
                <ArrowUpRight className="arrow w-4 h-4" strokeWidth={1.25} />
              </button>

              <p className="mt-6 text-[0.7rem] sm:text-[0.78rem] font-sub uppercase tracking-[0.24em] text-[color:var(--tr-ink-soft)]">
                Esemplari numerati · Su prenotazione
              </p>
            </div>
          </div>
        </div>
      </div>

      {lightboxAt !== null && (
        <Lightbox
          images={images}
          startIndex={lightboxAt}
          title={`${product.name} — N° ${product.code}`}
          onClose={() => setLightboxAt(null)}
        />
      )}
    </div>
  );
}
